import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Badge, IconButton } from '@mui/material';
import AuthService from '../../services/auth.service';
import { StateContext } from '../context/StateProvider';

// Intervalo de refresco por defecto (mismo criterio que otros sondeos de la app)
const REFRESCO_MS = 120000;

/**
 * Item genérico de header-nav con icono, badge contador y navegación
 * Visible solo para los roles indicados; el contador se refresca periódicamente
 * @param {object} icon - Componente de icono de @mui/icons-material
 * @param {string} ruta - Ruta a la que navegar al pulsar
 * @param {string} titulo - Texto plural para el tooltip (ej: "incidencias abiertas")
 * @param {string[]} roles - Roles con acceso al icono (los de la ruta destino)
 * @param {Function} cargarContador - Función async que devuelve el número a mostrar (referencia estable)
 * @param {string} color - Color del badge MUI (error, warning...)
 * @param {boolean} compacto - Versión reducida para el header plegado
 * @param {string} triggerKey - Clave en state.refrescarContadores para refrescos manuales (opcional)
 */
export default function HeaderContador({ icon: Icon, ruta, titulo, roles, cargarContador, color = 'error', compacto = false, triggerKey }) {
    const navigate = useNavigate();
    const { state } = useContext(StateContext);
    const [contador, setContador] = useState(0);
    const permitido = AuthService.getUserRoles().some((rol) => roles.includes(rol));

    // Obtener el timestamp del trigger si existe
    const triggerTimestamp = triggerKey ? state.refrescarContadores?.[triggerKey] : null;

    useEffect(() => {
        if (!permitido) return;

        let alive = true;

        const cargar = async () => {
            try {
                const valor = await cargarContador();
                if (alive) setContador(valor);
            } catch (error) {
                console.error(`Error al cargar contador de ${titulo}:`, error);
            }
        };

        cargar();
        const interval = setInterval(cargar, REFRESCO_MS);

        return () => {
            alive = false;
            clearInterval(interval);
        };
    }, [permitido, cargarContador, titulo, triggerTimestamp]);

    if (!permitido) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
                size="small"
                title={`${contador} ${titulo}`}
                onClick={() => navigate(ruta)}
                sx={compacto ? { p: 0.5 } : undefined}
            >
                <Badge
                    badgeContent={contador}
                    color={color}
                    max={99}
                    sx={compacto ? { '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } } : undefined}
                >
                    <Icon sx={{ fontSize: compacto ? 19 : 24, color: '#6c757d' }} />
                </Badge>
            </IconButton>
        </Box>
    );
}
